import { useCallback, useRef, useState } from "react";
import Webcam from "react-webcam";
import { useQueryParams } from "../../../utils/hooks/useQueryParams";
import { logger } from "../../../utils/logger";
import { telegramApi } from "../../../utils/telegramApi";
import { useCameraDevices } from "../../utils/hooks/useCameraDevices";
import { useGeolocation } from "../../utils/hooks/useGeolocation";
import { useOperationData } from "../../utils/hooks/useOperationData";
import { usePhotoCollection } from "../../utils/hooks/usePhotoCollection";
import { usePhotoUpload } from "../../utils/hooks/usePhotoUpload";
import { useTelegramButton } from "../../utils/hooks/useTelegramButton";
import { useTorchControl } from "../../utils/hooks/useTorchControl";
import { GeolocationPanel } from "../GeolocationPanel/GeolocationPanel";
import { Photo } from "../Photo/Photo";
import styles from "./styles.module.sass";

/**
 * @fileoverview Главный компонент Telegram Web App для создания фотографий с водяными знаками.
 * Управляет камерой, геолокацией, обработкой изображений и интеграцией с Telegram API.
 *
 * @since 1.0.0
 */

/**
 * Основной компонент приложения камеры Telegram Web App.
 *
 * Обеспечивает полный цикл работы с фотографиями:
 * - Инициализация камеры с fallback механизмами
 * - Получение геолокации через Telegram API с browser fallback
 * - Два режима работы: съемка и просмотр фотографий
 * - Добавление водяных знаков с геоданными
 * - Загрузка в S3 хранилище через pre-signed URLs
 * - Интеграция с главной кнопкой Telegram
 *
 * @remarks
 * Компонент следует принципу "режим съемки по умолчанию" - пользователь остается
 * в режиме съемки после каждого снимка. Переход в просмотр происходит только при
 * явном выборе фотографии или достижении максимального количества.
 *
 * Геолокация задерживается до получения доступа к камере для улучшения UX
 * и обхода бага react-webcam при одновременном запросе разрешений.
 *
 * @returns JSX элемент с интерфейсом камеры и управлением фотографиями
 *
 * @example
 * ```tsx
 * // Использование в Telegram Web App
 * <WebAppCam />
 * ```
 */
export function WebAppCam() {
  const webcamRef = useRef<Webcam>(null);

  // Состояние камеры
  const [cameraError, setCameraError] = useState<string>("");
  const [cameraInitializing, setCameraInitializing] = useState(true);
  const [geolocationDelayed, setGeolocationDelayed] = useState(true);

  // Хуки для бизнес-логики
  const params = useQueryParams();
  const { operationData } = useOperationData(params?.operation_id);
  const {
    cameras,
    isFrontCamera,
    retryAttempt,
    videoConstraints,
    switchCamera,
    retryCamera,
  } = useCameraDevices();
  const {
    images,
    selectedPhoto,
    addImage,
    selectImage,
    deleteImage,
    clearSelection,
    canAddMore,
  } = usePhotoCollection(operationData);
  const { isProcessing, uploadPhotos } = usePhotoUpload();
  const {
    location,
    error: locationError,
    status: locationStatus,
    retry: retryLocation,
  } = useGeolocation(geolocationDelayed);
  const {
    isTorchSupported,
    isTorchOn,
    isCheckingSupport,
    toggleTorch,
    resetTorchState,
  } = useTorchControl(webcamRef);

  /**
   * Захватывает фотографию с камеры и добавляет в список изображений.
   */
  const capture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        addImage(imageSrc);
      }
    }
  }, [addImage]);

  /**
   * Обертка для загрузки фотографий на сервер.
   */
  const handleSendToServer = useCallback(async () => {
    if (
      !params?.operation_id ||
      !params?.task_element_id ||
      !params?.doer_user_id
    ) {
      return;
    }

    await uploadPhotos(images, location, operationData, {
      operation_id: params.operation_id,
      task_element_id: params.task_element_id,
      doer_user_id: params.doer_user_id,
    });
  }, [images, location, operationData, params, uploadPhotos]);

  // Управление Telegram кнопкой
  useTelegramButton(images, location, operationData, handleSendToServer);

  return (
    <div className={styles.container}>
      <GeolocationPanel
        status={locationStatus}
        error={locationError}
        onRetry={retryLocation}
        onShowInfo={() => {
          const message =
            locationError ||
            "Ошибка получения геолокации. Убедитесь, что разрешили доступ к местоположению.";
          telegramApi.showAlert(message);
        }}
      />

      {/* Показываем лоадер во время обработки */}
      {isProcessing ? (
        <div className={styles.processingContainer}>
          <div className={styles.loader}>
            <div className={styles.loaderSpinner}></div>
          </div>
        </div>
      ) : (
        <div className={styles.mainContent}>
          {/* Список фотографий - показываем контейнер всегда для стабильности макета */}
          <div className={styles.imageContainer}>
            {images.map((image, index) => (
              <Photo
                key={index}
                src={image}
                onClick={() => selectImage(image)}
                onDelete={() => deleteImage(index)}
                isSelected={selectedPhoto === image}
              />
            ))}
          </div>

          {/* Область камеры - скрываем CSS стилями в режиме просмотра */}
          <div
            className={`${styles.cameraWrapper} ${
              selectedPhoto ? styles.hidden : ""
            }`}
          >
            {/* React Webcam - показываем сразу */}
            {cameras.length > 0 && (
              <Webcam
                key={`camera-${
                  isFrontCamera ? "front" : "back"
                }-${retryAttempt}`}
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                screenshotQuality={0.9}
                imageSmoothing={true}
                forceScreenshotSourceSize={true}
                videoConstraints={videoConstraints}
                className={styles.webcam}
                playsInline={true}
                onUserMedia={() => {
                  setCameraError("");
                  setCameraInitializing(false);
                  setGeolocationDelayed(false);
                  // Сбрасываем состояние фонарика при инициализации камеры
                  resetTorchState();
                }}
                onUserMediaError={(error) => {
                  const errorName =
                    error instanceof DOMException
                      ? error.name
                      : "Unknown error";
                  const errorMessage =
                    error instanceof Error ? error.message : String(error);
                  logger.error("Ошибка onUserMedia", {
                    error: errorName,
                    message: errorMessage,
                  });

                  setCameraInitializing(false);
                  setCameraError(errorName);

                  // Для остальных ошибок просто показываем UI с кнопкой retry
                  // Не закрываем приложение, даем пользователю возможность повторить
                }}
              />
            )}

            {/* UI для ошибок камеры с кнопкой повтора */}
            {cameraError && !cameraInitializing && (
              <div className={styles.errorOverlay}>
                <div className={styles.errorCard}>
                  <div className={styles.errorIcon}>⚠️</div>
                  <div className={styles.errorTitle}>Проблема с камерой</div>
                  <div className={styles.errorDescription}>
                    {cameraError === "NotAllowedError" &&
                      "Нет доступа к камере. Разрешите использование камеры в браузере."}
                    {cameraError === "NotFoundError" &&
                      "Камера не найдена. Убедитесь, что камера подключена и доступна."}
                    {cameraError === "OverconstrainedError" &&
                      "Камера не поддерживает требуемые параметры."}
                    {![
                      "NotAllowedError",
                      "NotFoundError",
                      "OverconstrainedError",
                    ].includes(cameraError) && `Ошибка камеры: ${cameraError}`}
                  </div>
                  <button onClick={retryCamera} className={styles.retryButton}>
                    Попробовать еще раз
                  </button>
                </div>
              </div>
            )}

            {/* UI для инициализации камеры */}
            {cameraInitializing && (
              <div className={styles.initializingOverlay}>
                <div className={styles.initializingIcon}>📷</div>
                <div className={styles.initializingText}>
                  Инициализация камеры...
                </div>
              </div>
            )}

            {/* Тулбар кнопок управления камерой */}
            <div className={styles.cameraControlsBar}>
              <div className={styles.cameraControlsContainer}>
                {/* Кнопка управления вспышкой/фонариком */}
                {isTorchSupported && (
                  <button
                    className={styles.btnTorchControl}
                    onClick={toggleTorch}
                    disabled={isCheckingSupport}
                    title={isTorchOn ? "Выключить фонарик" : "Включить фонарик"}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="28px"
                      height="28px"
                      viewBox="0 0 24 24"
                      version="1"
                    >
                      {isTorchOn ? (
                        // Иконка включенной вспышки
                        <g className={styles.torchIconOn}>
                          <path d="M7,2V13H10V22L17,10H13L17,2H7Z" />
                        </g>
                      ) : (
                        // Иконка выключенной вспышки
                        <g className={styles.torchIcon}>
                          <path d="M7,2V13H10V22L17,10H13L17,2H7Z" />
                        </g>
                      )}
                    </svg>
                  </button>
                )}

                {/* Кнопка смены камеры */}
                <button
                  className={styles.btnChangeCamera}
                  onClick={switchCamera}
                  title={`Переключить на ${
                    isFrontCamera ? "заднюю" : "переднюю"
                  } камеру`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28px"
                    height="28px"
                    viewBox="0 0 48 42"
                    version="1"
                  >
                    <g className={styles.svgG}>
                      <path d="M33.9,12.1H14.2L17.6,7c0.4-0.6,1-0.9,1.7-0.9h9.6c0.7,0,1.3,0.3,1.7,0.9L33.9,12.1z" />
                      <path d="M14,11H8V9.2C8,8.5,8.5,8,9.2,8h3.6C13.5,8,14,8.5,14,9.2V11z" />
                      <path d="M40,42H8c-2.2,0-4-1.8-4-4V14c0-2.2,1.8-4,4-4h32c2.2,0,4,1.8,4,4v24C44,40.2,42.2,42,40,42z" />
                    </g>
                    <path
                      fill="rgb(255,255,255)"
                      d="M34,25c0-5.5-4.5-10-10-10c-2.4,0-4.6,0.8-6.3,2.2l1.2,1.6c1.4-1.1,3.1-1.8,5.1-1.8c4.4,0,8,3.6,8,8h-3.5 l4.5,5.6l4.5-5.6H34z"
                    />
                    <path
                      fill="rgb(255,255,255)"
                      d="M29.1,31.2C27.7,32.3,25.9,33,24,33c-4.4,0-8-3.6-8-8h3.5L15,19.4L10.5,25H14c0,5.5,4.5,10,10,10 c2.4,0,4.6-0.8,6.3-2.2L29.1,31.2z"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Кнопка фотографирования */}
            {!cameraError && (
              <div className={styles.btnContainer}>
                <button className={styles.captureButton} onClick={capture}>
                  <div className={styles.outerCircle}>
                    <div className={styles.innerCircle}></div>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* Режим просмотра выбранного фото */}
          {selectedPhoto && (
            <div className={styles.lastImageMain}>
              {/* Выбранное фото крупно */}
              <img
                src={selectedPhoto}
                alt="Выбранное фото"
                className={styles.selectedPhoto}
              />

              {/* Плавающая кнопка добавления фото */}
              {canAddMore && (
                <button
                  className={styles.floatingAddButton}
                  onClick={clearSelection}
                  title="Добавить фото"
                >
                  +
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
